'use client';
import React, { useEffect, useRef } from 'react';

interface ServiceTier {
  name: string;
  priceRange: string;
  duration: string;
  tag: string;
  tagBg: string;
  tagColor: string;
  accentColor: string;
  borderColor: string;
  features: string[];
  featured?: boolean;
}

const tiers: ServiceTier[] = [
  {
    name: 'Basic Bundle',
    priceRange: '$175–$235',
    duration: '150–180 min',
    tag: 'Great Start',
    tagBg: 'rgba(0,245,255,0.08)',
    tagColor: '#00f5ff',
    accentColor: '#00f5ff',
    borderColor: 'rgba(0,245,255,0.3)',
    features: [
      'Steam cleaned seats, hand washed interior panels',
      'Carpets professionally vacuumed',
      'All glass cleaned in and out, door jambs cleaned',
      'Trunk vacuumed, air vents dusted',
      'Complimentary air freshener',
      'Exterior foam soak + hand wash',
      'Paint protected with wax, basic wheel clean',
      'Tire shine applied',
    ],
  },
  {
    name: 'Premium Bundle',
    priceRange: '$230–$350',
    duration: '180–270 min',
    tag: 'Most Popular',
    tagBg: '#00f5ff',
    tagColor: '#050510',
    accentColor: '#00f5ff',
    borderColor: '#00f5ff',
    featured: true,
    features: [
      'Full steam clean of seats with fabric protection or leather conditioning',
      'Deep vacuum + Tornador treatment of carpets, mats, vents, crevices, trunk',
      'Thorough cleaning of all interior panels, doors, console, door jambs',
      'Streak-free interior glass',
      'Foam soak + hand wash with iron remover + light clay bar',
      'Wheels cleaned (faces + accessible barrels) with tire shine',
      'Paint protected with quality sealant + exterior trim dressed',
      'Door jambs and exterior glass cleaned',
    ],
  },
  {
    name: 'Luxe Package',
    priceRange: '$330–$430',
    duration: '270–375 min',
    tag: 'Maximum Detail',
    tagBg: 'rgba(123,47,255,0.15)',
    tagColor: '#7b2fff',
    accentColor: '#7b2fff',
    borderColor: '#7b2fff',
    features: [
      'Full steam extraction of seats, carpets, and floor mats',
      'Complete Tornador treatment of vents, crevices, seat belts',
      'Deep cleaning + conditioning of all interior surfaces',
      'Leather fully conditioned or fabric protected + light odor treatment',
      'Trunk and cargo area fully detailed',
      'Foam soak + multi-stage hand wash with iron remover + full clay bar',
      'Wheels cleaned thoroughly (faces + barrels) with tire + wheel well dressing',
      'Paint hand-enhanced with high-grade sealant',
      'All exterior trim, plastics, and exhaust tips dressed',
      'Full door jambs, fuel door, and exterior glass streak-free',
    ],
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.classList.add('hidden-initial');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.remove('hidden-initial');
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" ref={sectionRef} className="z-reveal py-12 md:py-16">
      {/* Section header */}
      <div className="mb-8 md:mb-10">
        <div
          className="inline-block px-3 py-1 mb-3"
          style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.4)', boxShadow: '0 0 10px rgba(0,245,255,0.15)' }}
        >
          <span
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 900,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: '#00f5ff',
            }}
          >
            Service Packages
          </span>
        </div>
        <h2
          className="z-section-heading"
          style={{ fontSize: 'clamp(32px, 6vw, 56px)', lineHeight: 0.95, marginBottom: '8px' }}
        >
          Pick Your Package
        </h2>
        <p
          style={{
            fontFamily: 'Barlow Condensed, Arial, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(18px, 3vw, 28px)',
            textTransform: 'uppercase',
            color: '#7b8ab8',
            letterSpacing: '0.04em',
          }}
        >
          We Bring the Detail to You.
        </p>
        <div
          className="mt-4 flex items-start gap-2 px-4 py-3 max-w-md"
          style={{ background: 'rgba(255,0,255,0.06)', border: '1px solid rgba(255,0,255,0.2)' }}
        >
          <span style={{ fontSize: '16px', marginTop: '1px' }}>⚠️</span>
          <p style={{ fontSize: '13px', color: '#a0b0d8', fontWeight: 500 }}>
            Customer must provide water access at their location.
          </p>
        </div>
      </div>

      {/* Cards grid */}
      <div className="flex flex-col md:grid md:grid-cols-3 gap-4 md:gap-5">
        {tiers.map((tier) => (
          <div
            key={tier.name}
            className="z-package-card flex flex-col"
            style={{
              border: tier.featured ? `2px solid ${tier.borderColor}` : `1px solid ${tier.borderColor}`,
              position: 'relative',
              boxShadow: tier.featured ? `0 0 30px rgba(0,245,255,0.15)` : 'none',
            }}
          >
            {/* Featured top bar */}
            {tier.featured && (
              <div
                style={{
                  background: 'linear-gradient(90deg, #00f5ff, #7b2fff)',
                  padding: '6px 16px',
                  textAlign: 'center',
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: '#050510',
                }}
              >
                ★ Most Popular ★
              </div>
            )}

            <div className="p-5 md:p-6 flex flex-col flex-1">
              {/* Tag + price row on mobile */}
              <div className="flex items-center justify-between mb-4">
                <span
                  style={{
                    background: tier.tagBg,
                    color: tier.tagColor,
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    padding: '4px 10px',
                    border: `1px solid ${tier.borderColor}`,
                  }}
                >
                  {tier.tag}
                </span>
                {/* Price shown inline on mobile */}
                <span
                  className="md:hidden"
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '22px',
                    color: tier.accentColor,
                    lineHeight: 1,
                    textShadow: `0 0 12px ${tier.accentColor}60`,
                  }}
                >
                  {tier.priceRange}
                </span>
              </div>

              {/* Name */}
              <h3
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: '22px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  color: '#e0e8ff',
                  lineHeight: 1.1,
                  marginBottom: '12px',
                }}
              >
                {tier.name}
              </h3>

              {/* Price — hidden on mobile (shown above) */}
              <div className="hidden md:block mb-1">
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '38px',
                    color: tier.accentColor,
                    lineHeight: 1,
                    textShadow: `0 0 16px ${tier.accentColor}60`,
                  }}
                >
                  {tier.priceRange}
                </span>
              </div>
              <div
                className="flex items-center gap-1.5 mb-5"
                style={{ fontSize: '12px', color: '#7b8ab8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {tier.duration}
              </div>

              {/* Divider */}
              <div className="z-divider-light mb-4" />

              {/* Features */}
              <ul className="space-y-2 flex-grow mb-6">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span style={{ color: tier.accentColor, fontSize: '12px', marginTop: '2px', flexShrink: 0 }}>◆</span>
                    <span style={{ fontSize: '13px', color: '#a0b0d8', lineHeight: 1.5 }}>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href={tier.featured ? '/waitlist' : '/home#quote'}
                className={tier.featured ? 'z-btn-yellow w-full flex items-center justify-center' : 'z-btn-black w-full flex items-center justify-center'}
                style={{ minHeight: '44px', fontSize: '13px', textDecoration: 'none' }}
              >
                {tier.featured ? 'Join Waitlist' : 'Get a Quote'}
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}